import { useState, useEffect, useRef, useCallback } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  Timestamp,
  limit,
} from 'firebase/firestore';
import { getSupportFirestore, COLLECTIONS } from '../services/firebaseConfig';
import { getClubId } from 'src/api/CalendarBooking';

interface UseSupportUnreadCountProps {
  onHasUnreadChange?: (hasUnread: boolean) => void;
}

/**
 * Simple hook to track if there are any unread support messages
 * Shows a dot indicator instead of counting
 */
export function useSupportUnreadCount({ onHasUnreadChange }: UseSupportUnreadCountProps = {}) {
  const [hasUnread, setHasUnread] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const lastReadTimestampRef = useRef<Timestamp | null>(null);
  const onHasUnreadChangeRef = useRef(onHasUnreadChange);

  // Keep callback ref updated
  useEffect(() => {
    onHasUnreadChangeRef.current = onHasUnreadChange;
  }, [onHasUnreadChange]);

  // Get user credentials with polling to ensure they're available
  const [credentials, setCredentials] = useState<{ clubId: string; userId: string } | null>(null);

  useEffect(() => {
    const checkCredentials = () => {
      const clubId = String(getClubId() || '');
      let userId = '';

      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          const user = JSON.parse(userData);
          userId = String(user.id || '');
        }
      } catch {
        // Ignore parse errors
      }

      if (clubId && userId) {
        setCredentials({ clubId, userId });
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkCredentials()) return;

    // Poll every 500ms until credentials are available (for up to 10 seconds)
    let attempts = 0;
    const maxAttempts = 20;
    const interval = setInterval(() => {
      attempts++;
      if (checkCredentials() || attempts >= maxAttempts) {
        clearInterval(interval);
        if (attempts >= maxAttempts) {
          setIsLoading(false);
        }
      }
    }, 500);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!credentials) {
      return;
    }

    const { clubId, userId } = credentials;
    const db = getSupportFirestore();
    const unsubscribers: (() => void)[] = [];

    // Listen to lastReadTimestamp
    const statusRef = doc(db, COLLECTIONS.userChatStatus(clubId), userId);
    const unsubStatus = onSnapshot(
      statusRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.lastReadTimestamp instanceof Timestamp) {
            lastReadTimestampRef.current = data.lastReadTimestamp;
          }
        }
      },
      (err) => console.error('Error listening to lastReadTimestamp:', err)
    );
    unsubscribers.push(unsubStatus);

    // Listen to latest agent message only
    const messagesColRef = collection(db, COLLECTIONS.messages(clubId));
    const q = query(
      messagesColRef,
      orderBy('timestamp', 'desc'),
      limit(50) // Check last 50 messages for any unread agent message for this user
    );

    const unsubMessages = onSnapshot(
      q,
      (snapshot) => {
        let foundUnread = false;
        const lastRead = lastReadTimestampRef.current;

        snapshot.forEach((docSnap) => {
          if (foundUnread) return; // Already found one, skip rest

          const data = docSnap.data();
          const msgUserId = data.chatId || data.userId || '';
          const timestamp = data.timestamp instanceof Timestamp ? data.timestamp : null;

          // Check if this message is for this user and is from agent/system
          if (String(msgUserId) === userId &&
              (data.sender === 'agent' || data.sender === 'system') &&
              timestamp) {
            // Check if it's newer than lastRead
            if (!lastRead || timestamp.toMillis() > lastRead.toMillis()) {
              foundUnread = true;
            }
          }
        });

        setHasUnread(foundUnread);
        setIsLoading(false);
        if (onHasUnreadChangeRef.current) {
          onHasUnreadChangeRef.current(foundUnread);
        }
      },
      (err) => {
        console.error('Error fetching support messages:', err);
        setIsLoading(false);
      }
    );
    unsubscribers.push(unsubMessages);

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [credentials]);

  return { hasUnread, isLoading };
}

export default useSupportUnreadCount;
